export type RichTextBlock = ListBlock | TextBlock;

export enum ListBlockTypes {
  Checked = 'list:checked',
  Ordered = 'list:ordered',
  Unordered = 'list:unordered',
}
export interface ListBlock {
  blockType: ListBlockTypes;
  items: Text[];
}

export enum TextBlockTypes {
  Heading = 'heading',
  Heading2 = 'heading:level-2',
  Quote = 'quote',
  Paragraph = 'paragraph',
}

export interface TextBlock {
  blockType: TextBlockTypes;
  text: Text;
}

export interface Text {
  value: string;
  markup?: Markup[];
}

export interface Markup {
  type: MarkupOptions;
  offset: number;
  length: number;
  symbol?: string;
  uri?: string;
  expandedUri?: string;
  theme?: string;
  color?: string;
}

export enum MarkupOptions {
  Italic = 'style:em',
  Bold = 'style:strong',
  SuperScript = 'style:superscript',
  SubScript = 'style:subscript',
  Highlight = 'style:highlight',
  Link = 'link:external',
  LinkInternal = 'link:internal',
}

export interface BaseComponent {
  type: string;
  subtype?: string;
  presentationProperties?: {
    intention?: string;
    variant?: string;
    tag?: string;
    altText?: string;
    addLayout?: string;
    style?: string;
    description?: string;
    number?: string;
    telephone?: string;
  };
  id?: string;
}

export type Component =
  | ImageComponent
  | LeadTextComponent
  | TextComponent
  | GradeComponent
  | UrlComponent
  | SubheadingComponent
  | GalleryComponent
  | FactComponent
  | VideoAssetComponent
  | AdComponent
  | InternalLinkComponent
  | BreakpointComponent
  | TableComponent
  | IntegrationEnrichmentComponent;

export interface TextComponent extends BaseComponent {
  type: 'text';
  paragraphs: RichTextBlock[];
}

export interface LeadTextComponent extends TextComponent {
  subtype: 'lead';
}

export interface ImageAsset {
  id: string;
  size?: ImageSize;
  hotspot?: Hotspot;
  main?: string;
}

export interface Hotspot {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ImageSize {
  width: number;
  height: number;
}

export interface ContactDetail {
  type: 'email' | 'web' | 'twitter' | 'facebook' | 'phone' | 'title' | 'other';
  value: string;
}

export interface Originator {
  id: string;
  title?: string;
  slug?: string;
  contacts: ContactDetail[];
  description?: string;
  image?: string;
  imageAsset?: ImageAsset;
  type?: string;
  workingTitle?: string;
  enabled?: boolean;
}

export interface ImageComponent extends BaseComponent {
  type: 'image';
  byline?: Originator;
  imageAsset: ImageAsset;
  altText?: string;
}

export interface InternalLinkComponent extends BaseComponent {
  type: 'teaser';
  id: string;
  slug: string;
  title: string;
  image: ImageAsset;
}

export interface GalleryImageComponent extends BaseComponent {
  type: 'image';
  byline: { title: string };
  imageAsset: ImageAsset;
  caption?: { value: string };
}

export interface GradeComponent extends BaseComponent {
  type: 'grade';
  grade: number;
}

export interface BreakpointComponent extends BaseComponent {
  type: 'breakpoint';
}

export interface UrlComponent extends BaseComponent {
  type: 'url';
  url: string;
  data?: {
    type: string;
    source: string;
    customProperties: {
      videoId: string;
    };
  };
}

export interface FactComponent extends BaseComponent {
  type: 'fact';
  title: { value: string };
  paragraphs: RichTextBlock[];
}

export interface IntegrationEnrichmentComponent extends BaseComponent {
  type: 'integration-enrichment';
  source: string;
  embed: { html: string };
  customProperties: { title: string };
  url: string;
}

export interface SubheadingComponent extends BaseComponent {
  type: 'subheading';
  text: { value: string };
}

export interface GalleryComponent extends BaseComponent {
  type: 'gallery';
  components: GalleryImageComponent[];
}

export interface VerticalFadeGalleryComponent extends BaseComponent {
  type: 'gallery';
  layout?: string;
  components: (GalleryImageComponent | VideoAssetComponent)[];
}

export interface TableComponent extends BaseComponent {
  type: 'table';
  rows: string[][];
  headers: { rows: string[]; columns: string[] };
}

export interface VideoImageAsset extends ImageAsset {
  snapshots?: string;
}

export interface VideoAssetComponent extends BaseComponent {
  type: 'video';
  videoAsset: {
    size?: VideoSize;
    metadata: {
      aspectRatio: string;
    };
    id: string;
    sourceFiles: SourceFile[];
    streamUrls: VideoStreamUrls;
    subtitles?: SubtitlesEntity[];
  };
  imageAsset: VideoImageAsset;
  provider: {
    id: string;
  };
  title?: Text;
  description?: Text;
}

interface VideoSize {
  width: number;
  height: number;
}

export interface VideoStreamUrls {
  hls: string;
  hds?: string;
  mp4?: string;
  pseudostreaming?: string[];
}

interface SourceFile {
  width: number;
  height: number;
}

export interface SubtitlesEntity {
  language: string;
  url: string;
  default?: boolean;
}

export interface AdComponent extends BaseComponent {
  id: string;
  type: 'ad';
  cssClass: string;
  sizes: number[][];
  position: number;
}
